import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface TransferDestination {
  id: string;
  name: string;
  slug: string;
  title: string;
  description: string | null;
  hero_image: string | null;
  content: string | null;
  highlights: string[];
  popular_routes: Array<{
    name: string;
    time: string;
    price: number;
  }>;
  gallery_images: string[];
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  is_active: boolean;
  display_order: number;
  from_airport_price: number | null;
  to_airport_price: number | null;
  estimated_time: string | null;
  created_at: string;
  updated_at: string;
}

export const useTransferDestinations = () => {
  return useQuery({
    queryKey: ["transfer-destinations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transfer_destinations")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as TransferDestination[];
    },
  });
};

export const useActiveTransferDestinations = () => {
  return useQuery({
    queryKey: ["transfer-destinations", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transfer_destinations")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as TransferDestination[];
    },
  });
};

export const useTransferDestination = (slug: string) => {
  return useQuery({
    queryKey: ["transfer-destination", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transfer_destinations")
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;
      return data as TransferDestination | null;
    },
    enabled: !!slug,
  });
};

export const useCreateTransferDestination = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (destination: Omit<TransferDestination, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("transfer_destinations")
        .insert(destination)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfer-destinations"] });
      toast({
        title: "Destino creado",
        description: "El destino se ha creado exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al crear el destino: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateTransferDestination = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<TransferDestination> }) => {
      const { data, error } = await supabase
        .from("transfer_destinations")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfer-destinations"] });
      toast({
        title: "Destino actualizado",
        description: "El destino se ha actualizado exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al actualizar el destino: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteTransferDestination = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("transfer_destinations")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfer-destinations"] });
      toast({
        title: "Destino eliminado",
        description: "El destino se ha eliminado exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al eliminar el destino: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};